import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-sidebar.css?inline';

/**
 * `<pk-sidebar>` — tile-grouped navigation sidebar with auto-active-state detection. v0.5
 *
 * Children pattern (any nesting):
 *   <section>
 *     <h6>Group title</h6>
 *     <nav>
 *       <a href="/portal/dashboard">Dashboard</a>
 *       ...
 *     </nav>
 *   </section>
 *
 * The component adds `class="active"` + `aria-current="page"` to the best-matching
 * link based on `window.location.pathname`. "Best match" is: exact match wins, then
 * longest-prefix match. A parent path (/portal) is never matched against a deeper
 * path (/portal/dashboard) — the prefix must account for boundary (trailing slash or end).
 *
 * Attributes (v0.5 additions):
 *   `surface`         — enum: `default` | `customer` | `admin`. Applied as `data-surface`
 *                        on the host root. Drives dark-tile vs light-tile theming.
 *   `open`            — boolean. At <=720px, controls overlay visibility. At >720px, implicit.
 *   `return-focus-to` — id of element to focus on mobile-close. Falls back to previously-active element.
 *
 * Events:
 *   `pk-sidebar-open`  — fires when mobile overlay becomes visible.
 *   `pk-sidebar-close` — fires when mobile overlay dismisses. detail: { reason: string }
 *
 * Slots:
 *   - default  — the <section>/<nav>/<a> tree
 *   - header   — optional area above the link tree
 *   - footer   — optional area below the link tree
 */
export class PkSidebar extends HTMLElement {
    static readonly tagName = 'pk-sidebar';

    static get observedAttributes(): string[] {
        return ['open', 'surface', 'return-focus-to'];
    }

    private readonly _root: ShadowRoot;
    private readonly _slotChangeHandler = (): void => this._applyActiveState();
    private readonly _popstateHandler   = (): void => this._applyActiveState();
    private readonly _keyHandler: (e: KeyboardEvent) => void;
    private readonly _focusInHandler: (e: FocusEvent) => void;
    private _previouslyFocused: Element | null = null;
    private _openSideEffectsScheduled = false;
    private _closingInProgress = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });

        this._keyHandler = (e: KeyboardEvent): void => {
            if (!this._isMobileOpen()) return;
            if (e.key === 'Escape') {
                this._closeOverlay('escape');
            }
        };

        this._focusInHandler = (e: FocusEvent): void => {
            if (!this._isMobileOpen()) return;
            if (e.target instanceof Node && !this._root.contains(e.target) && !this.contains(e.target)) {
                e.preventDefault();
                this._trapFocus();
            }
        };
    }

    connectedCallback(): void {
        this._doRender();
        const slot = this._root.querySelector('slot:not([name])');
        if (slot) {
            slot.addEventListener('slotchange', this._slotChangeHandler);
        }
        if (typeof globalThis.window !== 'undefined') {
            globalThis.window.addEventListener('popstate', this._popstateHandler);
        }
        document.addEventListener('keydown', this._keyHandler);
        if (this.hasAttribute('open')) {
            this._scheduleOpenSideEffects();
        }
        this._applySurface();
    }

    disconnectedCallback(): void {
        const slot = this._root.querySelector('slot:not([name])');
        if (slot) {
            slot.removeEventListener('slotchange', this._slotChangeHandler);
        }
        if (typeof globalThis.window !== 'undefined') {
            globalThis.window.removeEventListener('popstate', this._popstateHandler);
        }
        document.removeEventListener('keydown', this._keyHandler);
        document.removeEventListener('focusin', this._focusInHandler);
    }

    attributeChangedCallback(name: string, old: string | null, next: string | null): void {
        this._doRender();
        if (name === 'open') {
            if (next !== null && old === null) {
                this._scheduleOpenSideEffects();
            } else if (next === null && old !== null) {
                // Only fire programmatic close if not already being closed via _closeOverlay
                if (!this._closingInProgress) {
                    this._onCloseSideEffects('programmatic');
                }
            }
        }
        if (name === 'surface') {
            this._applySurface();
        }
    }

    // ── Surface attribute ──────────────────────────────────────────────────────

    private _applySurface(): void {
        const surface = this.getAttribute('surface');
        if (surface) {
            this.dataset['surface'] = surface;
        } else {
            delete this.dataset['surface'];
        }
    }

    // ── Mobile open/close state ────────────────────────────────────────────────

    private _isMobileOpen(): boolean {
        return this.hasAttribute('open') && this._isMobileViewport();
    }

    private _isMobileViewport(): boolean {
        return typeof globalThis.window !== 'undefined' && globalThis.window.innerWidth <= 720;
    }

    // ── Lifecycle side-effects ─────────────────────────────────────────────────

    private _scheduleOpenSideEffects(): void {
        if (this._openSideEffectsScheduled) return;
        this._openSideEffectsScheduled = true;
        requestAnimationFrame(() => {
            this._openSideEffectsScheduled = false;
            if (this.hasAttribute('open') && this._isMobileViewport()) {
                this._onOpenSideEffects();
            }
        });
    }

    private _onOpenSideEffects(): void {
        this._previouslyFocused = document.activeElement;
        document.addEventListener('focusin', this._focusInHandler);
        this._trapFocus();
        this.dispatchEvent(new CustomEvent('pk-sidebar-open', {
            bubbles: true,
            composed: true,
        }));
    }

    private _onCloseSideEffects(reason: 'backdrop' | 'escape' | 'close-button' | 'programmatic'): void {
        document.removeEventListener('focusin', this._focusInHandler);
        this._returnFocus();
        this.dispatchEvent(new CustomEvent('pk-sidebar-close', {
            bubbles: true,
            composed: true,
            detail: { reason },
        }));
    }

    private _closeOverlay(reason: 'backdrop' | 'escape' | 'close-button' | 'programmatic'): void {
        if (!this.hasAttribute('open')) return;
        this._closingInProgress = true;
        this.removeAttribute('open');
        this._closingInProgress = false;
        this._onCloseSideEffects(reason);
    }

    // ── Focus trap ─────────────────────────────────────────────────────────────

    private _trapFocus(): void {
        const focusable = this._focusableElements();
        if (focusable.length > 0) {
            (focusable[0] as HTMLElement).focus();
        }
    }

    private _focusableElements(): Element[] {
        const selectors = [
            'button:not([disabled])',
            'a[href]',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(',');
        return Array.from(this._root.querySelectorAll(selectors));
    }

    private _handleTabKey(e: KeyboardEvent): void {
        if (e.key !== 'Tab') return;
        const focusable = this._focusableElements() as HTMLElement[];
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last  = focusable.at(-1);
        if (!first || !last) return;

        if (e.shiftKey) {
            if (this._root.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else if (this._root.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    private _returnFocus(): void {
        const returnId = this.getAttribute('return-focus-to');
        if (returnId) {
            const target = document.getElementById(returnId);
            if (target) {
                (target as HTMLElement).focus();
                this._previouslyFocused = null;
                return;
            }
        }
        if (this._previouslyFocused && typeof (this._previouslyFocused as HTMLElement).focus === 'function') {
            (this._previouslyFocused as HTMLElement).focus();
        }
        this._previouslyFocused = null;
    }

    // ── Active-state detection ─────────────────────────────────────────────────

    /**
     * Walks all slotted <a> elements and applies active state to the best match.
     */
    private _applyActiveState(): void {
        const pathname = typeof globalThis.window === 'undefined' ? '/' : globalThis.window.location.pathname;
        const slot = this._root.querySelector('slot:not([name])');
        if (!slot) return;

        // Collect all <a> elements from slotted content (deep, not just direct children)
        const anchors: HTMLAnchorElement[] = [];
        (slot as HTMLSlotElement).assignedElements({ flatten: true }).forEach((el) => {
            anchors.push(...Array.from(el.querySelectorAll<HTMLAnchorElement>('a[href]')));
            if (el instanceof HTMLAnchorElement && el.hasAttribute('href')) {
                anchors.push(el);
            }
        });

        // Clear any existing active state
        anchors.forEach((a) => {
            a.classList.remove('active');
            a.removeAttribute('aria-current');
        });

        if (anchors.length === 0) return;

        // Pick the best match: exact first, then longest valid prefix
        let bestAnchor: HTMLAnchorElement | null = null;
        let bestLength = -1;

        anchors.forEach((a) => {
            const href = a.getAttribute('href') ?? '';
            // Only consider same-origin paths
            if (!href.startsWith('/')) return;

            // Exact match
            if (href === pathname) {
                if (bestLength < href.length) {
                    bestAnchor = a;
                    bestLength = href.length;
                }
                return;
            }

            // Prefix match: href must be followed by '/' in pathname to avoid
            // matching /portal against /portal-admin or /portal/dashboard when
            // href is /portal/dash (partial).
            if (pathname.startsWith(href + '/')) {
                if (bestLength < href.length) {
                    bestAnchor = a;
                    bestLength = href.length;
                }
            }
        });

        if (bestAnchor) {
            (bestAnchor as HTMLAnchorElement).classList.add('active');
            (bestAnchor as HTMLAnchorElement).setAttribute('aria-current', 'page');
        }
    }

    // ── Template ───────────────────────────────────────────────────────────────

    private template(): TemplateResult {
        const isMobileOpen = this._isMobileOpen();

        return html`
            <style>${styles}</style>
            ${isMobileOpen ? html`<div class="backdrop" @click=${() => this._closeOverlay('backdrop')}></div>` : nothing}
            <div
                class="nav-content"
                role="navigation"
                aria-label="Portal navigation"
                aria-modal=${isMobileOpen ? 'true' : nothing}
                @keydown=${(e: KeyboardEvent) => this._handleTabKey(e)}
            >
                ${isMobileOpen ? html`
                    <button
                        type="button"
                        class="close-btn"
                        aria-label="Close navigation"
                        @click=${() => this._closeOverlay('close-button')}
                    >&#x2715;</button>
                ` : nothing}
                <slot name="header"></slot>
                <slot></slot>
                <slot name="footer"></slot>
            </div>
        `;
    }

    private _doRender(): void {
        render(this.template(), this._root);
        // Apply active state after initial render (slot not yet populated, but
        // slotchange will fire; this handles SSR-stamped content on the fast path).
        Promise.resolve().then(() => this._applyActiveState());
    }
}

if (!customElements.get(PkSidebar.tagName)) {
    customElements.define(PkSidebar.tagName, PkSidebar);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-sidebar': PkSidebar;
    }
}
